import React, { useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { PlusIcon, TrashIcon, XmarkIcon } from '@shared/assets/icons';
import { colors } from '@shared/tokens/colors.stylex';
import type { InviteFilterInput, TicketFilterInput, TicketsFilterInput } from '@pages/event/api/useFetchTicketSegment';
import { InvitationStatusTicketEnum } from '@pages/event/api/useFetchTicketSegment';
import { CustomDropdown } from '@shared/ui/dropdown';
import { GuestListFilter } from './ui/GuestListFilter';

interface ModalFilterProps {
  active: boolean;
  onClose: () => void;
  onApply?: (filters: TicketsFilterInput | undefined) => void;
}

type Id = number;
type SendId = 'SMS' | 'EMAIL';
type Boolish = true | false | null;

interface FilterRow {
  id: Id;
  sendMethod?: SendId | null;
  deliveryStatus?: Boolish;
  inviteStatus?: InvitationStatusTicketEnum | null;
  presenceStatus?: Boolish;
}

type SendOption = { id: SendId | null; title: string };
type InviteOption = { id: InvitationStatusTicketEnum | null; title: string };
type BoolOption = { id: Boolish; title: string };

// const BASE_ROW_Z = 1000;
const BASE_DROPDOWN_Z = 10000;
const Z_STEP = 10;

const MAX_TOTAL_ROWS_PER_BLOCK = 3;

type RowKind = 'invite' | 'ticket';
const ROW_KIND_INVITE: RowKind = 'invite';
const ROW_KIND_TICKET: RowKind = 'ticket';

type FilterRowViewProps = {
  kind: RowKind;
  row: FilterRow;
  index: number;
  inviteCount: number;
  ticketCount: number;
  isLast: boolean;
  isFirst: boolean;
  onUpdate: (id: Id, patch: Partial<FilterRow>) => void;
  onRemove: (id: Id) => void;
  onAdd: () => void;
  sendOptions: ReadonlyArray<SendOption>;
  deliveryOptions: ReadonlyArray<BoolOption>;
  inviteOptions: ReadonlyArray<InviteOption>;
  presenceOptions: ReadonlyArray<BoolOption>;
};
// TODO: Нужно переписать компонент согласно FSD
const FilterRowView: React.FC<FilterRowViewProps> = ({
  kind,
  row,
  index,
  inviteCount,
  ticketCount,
  isLast,
  isFirst,
  onUpdate,
  onRemove,
  onAdd,
  sendOptions,
  deliveryOptions,
  inviteOptions,
  presenceOptions,
}) => {
  const isInviteBlock = kind === ROW_KIND_INVITE;
  const globalOrder = isInviteBlock ? index : inviteCount + index;
  const dropdownZIndex = BASE_DROPDOWN_Z - globalOrder * Z_STEP;
  const canAddMore = isInviteBlock ? inviteCount < MAX_TOTAL_ROWS_PER_BLOCK : ticketCount < MAX_TOTAL_ROWS_PER_BLOCK;

  return (
    <div key={row.id} {...stylex.props(styles.sendInviteRow)}>
      <CustomDropdown<SendOption>
        labelMessage="Способ отправки"
        placeholder="Выберите значение"
        settings={{ zIndex: String(dropdownZIndex) }}
        options={sendOptions}
        itemText="title"
        itemValue="id"
        value={sendOptions.find((option) => option.id === row.sendMethod) ?? null}
        changeFunc={(option) => {
          onUpdate(row.id, { sendMethod: option?.id ?? null });
        }}
      />

      <CustomDropdown<BoolOption>
        labelMessage="Статус доставки"
        placeholder="Выберите значение"
        settings={{ width: '100%', zIndex: String(dropdownZIndex - 1) }}
        options={deliveryOptions}
        itemText="title"
        itemValue="id"
        value={deliveryOptions.find((option) => option.id === row.deliveryStatus) ?? null}
        changeFunc={(option) => onUpdate(row.id, { deliveryStatus: option?.id ?? null })}
      />

      {kind === ROW_KIND_INVITE ? (
        <CustomDropdown<InviteOption>
          labelMessage="Статус приглашения"
          placeholder="Выберите значение"
          settings={{ width: '100%', zIndex: String(dropdownZIndex - 2) }}
          options={inviteOptions}
          itemText="title"
          itemValue="id"
          value={inviteOptions.find((option) => option.id === row.inviteStatus) ?? null}
          changeFunc={(option) => onUpdate(row.id, { inviteStatus: option?.id ?? null })}
        />
      ) : (
        <CustomDropdown<BoolOption>
          labelMessage="Статус присутствия"
          placeholder="Выберите значение"
          settings={{ width: '100%', zIndex: String(dropdownZIndex - 2) }}
          options={presenceOptions}
          itemText="title"
          itemValue="id"
          value={presenceOptions.find((option) => option.id === row.presenceStatus) ?? null}
          changeFunc={(option) => onUpdate(row.id, { presenceStatus: option?.id ?? null })}
        />
      )}

      <div {...stylex.props(styles.iconButton, !isFirst && styles.iconButtonHover)}>
        <Button size="small" variant="secondary" onClick={() => onRemove(row.id)} isDisabled={isFirst}>
          <TrashIcon size="20" color={isFirst ? colors.tertiaryText : colors.primaryText} />
        </Button>
      </div>

      {isLast && (
        <div {...stylex.props(styles.iconButton, canAddMore && styles.iconButtonHover)}>
          <Button size="small" variant="secondary" onClick={onAdd} isDisabled={!canAddMore}>
            <PlusIcon color={colors.primaryIcon} />
          </Button>
        </div>
      )}
    </div>
  );
};

export const ModalFilter: React.FC<ModalFilterProps> = ({ active, onClose, onApply }) => {
  const idSeq = useRef(1);
  const nextId = () => {
    const currentId = idSeq.current;
    idSeq.current += 1;
    return currentId;
  };

  const [inviteRows, setInviteRows] = useState<FilterRow[]>([{ id: nextId() }]);
  const [ticketRows, setTicketRows] = useState<FilterRow[]>([{ id: nextId() }]);
  const [guestFilter, setGuestFilter] = useState<string | null>(null);

  const sendOptions: ReadonlyArray<SendOption> = useMemo(
    () => [
      { id: 'SMS', title: 'СМС' },
      { id: 'EMAIL', title: 'Email' },
      { id: null, title: 'Любой' },
    ],
    []
  );

  const deliveryOptions: ReadonlyArray<BoolOption> = useMemo(
    () => [
      { id: true, title: 'Отправлено' },
      { id: false, title: 'Не отправлено' },
      { id: null, title: 'Любой' },
    ],
    []
  );

  const inviteOptions: ReadonlyArray<InviteOption> = useMemo(
    () => [
      { id: InvitationStatusTicketEnum.SENT, title: 'Ждем ответа' },
      { id: InvitationStatusTicketEnum.ACCEPTED, title: 'Принято' },
      { id: InvitationStatusTicketEnum.DECLINED, title: 'Отклонено' },
      { id: null, title: 'Любой' },
    ],
    []
  );

  const presenceOptions: ReadonlyArray<BoolOption> = useMemo(
    () => [
      { id: true, title: 'Присутствовал' },
      { id: false, title: 'Не присутствовал' },
      { id: null, title: 'Любой' },
    ],
    []
  );

  const addRow = (setState: React.Dispatch<React.SetStateAction<FilterRow[]>>) => () =>
    setState((prev) => (prev.length < MAX_TOTAL_ROWS_PER_BLOCK ? [...prev, { id: nextId() }] : prev));

  const removeRow = (setState: React.Dispatch<React.SetStateAction<FilterRow[]>>) => (id: Id) =>
    setState((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  const updateRow =
    (setState: React.Dispatch<React.SetStateAction<FilterRow[]>>) => (id: Id, patch: Partial<FilterRow>) =>
      setState((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const clearAll = () => {
    setInviteRows([{ id: nextId() }]);
    setTicketRows([{ id: nextId() }]);
    setGuestFilter(null);
  };

  const hasDefinedValue = (value: unknown) => value !== null && value !== undefined;

  const mapInviteRows = (rows: FilterRow[]): InviteFilterInput[] | null => {
    const mapped = rows
      .map((row) => ({
        inviteSentMethod: row.sendMethod ?? null,
        inviteSent: row.deliveryStatus === null ? null : row.deliveryStatus ?? null,
        invitationStatus: row.inviteStatus ?? null,
      }))
      .filter(
        (item) =>
          hasDefinedValue(item.inviteSentMethod) ||
          hasDefinedValue(item.inviteSent) ||
          hasDefinedValue(item.invitationStatus)
      );

    return mapped.length ? mapped : null;
  };

  const mapTicketRows = (rows: FilterRow[]): TicketFilterInput[] | null => {
    const mapped = rows
      .map((row) => ({
        ticketSentMethod: row.sendMethod ?? null,
        ticketSent: row.deliveryStatus === null ? null : row.deliveryStatus ?? null,
        checked: row.presenceStatus === null ? null : row.presenceStatus ?? null,
      }))
      .filter(
        (item) =>
          hasDefinedValue(item.ticketSentMethod) || hasDefinedValue(item.ticketSent) || hasDefinedValue(item.checked)
      );

    return mapped.length ? mapped : null;
  };

  const mapToTicketsFilterInput = (inv: FilterRow[], tic: FilterRow[]): TicketsFilterInput => {
    const inviteFilter = mapInviteRows(inv);
    const ticketFilter = mapTicketRows(tic);

    return {
      inviteFilter,
      ticketFilter,
      orderBy: null,
      guestFilter,
    };
  };

  const apply = () => {
    const payload = mapToTicketsFilterInput(inviteRows, ticketRows);
    const hasFilters = !!(payload.inviteFilter?.length || payload.ticketFilter?.length || guestFilter);

    onApply?.(hasFilters ? payload : undefined);
    onClose();
  };

  return (
    <Modal
      active={active}
      settingsContainer={{ height: 'auto', padding: '0px', overflow: 'visible' }}
      settingsHeader={{ padding: '0px' }}
      settingsFooter={{ padding: '0px' }}
      settings={{ overflow: 'visible' }}
    >
      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.header)}>
          <p {...stylex.props(styles.headerText)}>Фильтр участников</p>
          <XmarkIcon size="24" onClick={onClose} />
        </div>

        {/* Приглашение */}
        <div {...stylex.props(styles.sendInvite)}>
          <p {...stylex.props(styles.sendInviteText)}>Приглашение</p>
          {inviteRows.map((row, index) => (
            <FilterRowView
              key={row.id}
              kind={ROW_KIND_INVITE}
              row={row}
              index={index}
              inviteCount={inviteRows.length}
              ticketCount={ticketRows.length}
              isLast={index === inviteRows.length - 1}
              isFirst={index === 0}
              onUpdate={updateRow(setInviteRows)}
              onRemove={removeRow(setInviteRows)}
              onAdd={addRow(setInviteRows)}
              sendOptions={sendOptions}
              deliveryOptions={deliveryOptions}
              inviteOptions={inviteOptions}
              presenceOptions={presenceOptions}
            />
          ))}
        </div>

        {/* Билет */}
        <div {...stylex.props(styles.sendInvite)}>
          <p {...stylex.props(styles.sendInviteText)}>Билет</p>
          {ticketRows.map((row, index) => (
            <FilterRowView
              key={row.id}
              kind={ROW_KIND_TICKET}
              row={row}
              index={index}
              inviteCount={inviteRows.length}
              ticketCount={ticketRows.length}
              isLast={index === ticketRows.length - 1}
              isFirst={index === 0}
              onUpdate={updateRow(setTicketRows)}
              onRemove={removeRow(setTicketRows)}
              onAdd={addRow(setTicketRows)}
              sendOptions={sendOptions}
              deliveryOptions={deliveryOptions}
              inviteOptions={inviteOptions}
              presenceOptions={presenceOptions}
            />
          ))}
        </div>

        {/* Список гостей */}
        <div {...stylex.props(styles.guestListFilter)}>
          <p {...stylex.props(styles.sendInviteText)}>Список гостей</p>
          <GuestListFilter guestFilter={guestFilter} onChange={setGuestFilter} />
        </div>

        <div {...stylex.props(styles.actionButton)}>
          <Button
            size="small"
            variant="secondary"
            settings={{ padding: '8px 20px', fontSize: '14px' }}
            onClick={clearAll}
          >
            Очистить
          </Button>
          <Button size="small" variant="accent" settings={{ padding: '8px 20px', fontSize: '14px' }} onClick={apply}>
            Применить
          </Button>
        </div>
      </div>
    </Modal>
  );
};
